import { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { isToday, isSameYear } from 'date-fns';

import { cx } from '@libs/classnames';
import { pluralize } from '@libs/i18n';
import { DateView } from '@ui/components/DateView';
import { SlimArrowButton } from '@ui/components/SlimArrowButton';
import { ArrowSlimIcon } from '@ui/icons/ArrowSlimIcon';
import { CertificateSealIcon } from '@ui/icons/CertificateSealIcon';

import styles from './PremiumSubscription.module.css';

export function PremiumSubscription({
  className,
  loading,
  timestampEnd,
  tournamentsLeft,
  linkInfo,
  linkEvents,
  linkPrices,
  isAdditional,
}: {
  className?: string;
  loading?: boolean;
  timestampEnd?: number | null;
  tournamentsLeft?: number | null;
  linkInfo: string;
  linkEvents: string;
  linkPrices: string;
  isAdditional?: boolean;
}) {
  const now = Date.now();
  const hasTournamentsCounter = typeof tournamentsLeft === 'number';
  const isActive =
    timestampEnd && timestampEnd > now && (!hasTournamentsCounter || tournamentsLeft > 0);
  const isExpiringToday = isActive && isToday(timestampEnd);
  const isFutureYear = Boolean(isActive && !isSameYear(timestampEnd, now));

  return (
    <section
      className={cx(styles.subscription, className, {
        [styles.is_not_active]: !loading && !isActive,
        [styles.is_skeleton]: loading,
      })}
    >
      {/* Шапка */}

      {!isAdditional && (
        <div className={styles.header}>
          <span className={styles.header__title}>Премиум-аккаунт ChessOpen</span>
          <Link to={linkInfo}>
            <SlimArrowButton colorPreset="golden" direction="right" size="20" />
          </Link>
        </div>
      )}

      {/* Контент */}

      <div className={styles.content}>
        <CertificateSealIcon className={styles.content__icon} />
        {!loading && (
          <div className={styles.content__data}>
            {isActive && (
              <p className={styles.content__data__title}>
                {hasTournamentsCounter ? (
                  <>
                    {pluralize(
                      {
                        one: `${isAdditional ? 'Плюс' : 'Остался'} {number} турнир`,
                        few: `${isAdditional ? 'Плюс' : 'Осталось'} {number} турнира`,
                        many: `${isAdditional ? 'Плюс' : 'Осталось'} {number} турниров`,
                      },
                      tournamentsLeft,
                    )}{' '}
                    до <DateView date={timestampEnd} year="notCurrent" shortMonth={isFutureYear} />
                  </>
                ) : isExpiringToday ? (
                  'Подписка истекает сегодня'
                ) : (
                  <>
                    Подписка активна до{' '}
                    <DateView date={timestampEnd} year="notCurrent" shortMonth={isFutureYear} />
                  </>
                )}
              </p>
            )}
            {!isActive && (
              <>
                <p className={styles.content__data__title}>Не активен</p>
                <p className={styles.content__data__body}>
                  <strong>Премиум-аккаунт доступен для юношей и девушек до 19 лет.</strong> Он дает
                  доступ к участию в турнирах Гран-при РШШ по блицу, Гран-при РШШ по рапиду, с
                  обсчетом рейтинга ФШР. Также с премиум-аккаунтом вы получаете доступ к одному
                  сеансу одновременной игры в месяц с гроссмейстерами и международными мастерами.
                  При реализации абонемента по числу турниров, сеанс одновременной игры
                  засчитывается как турнир.
                </p>
              </>
            )}
          </div>
        )}
        {loading && (
          <div className={styles.content__data}>
            <p className={styles.content__data__title}>Подписка загружается...</p>
          </div>
        )}
      </div>

      {/* Кнопки */}

      {!loading && !isAdditional && (
        <div className={styles.actions}>
          {isActive && (
            <>
              <SubscriptionLink theme="subscription" to={linkPrices}>
                Продлить
                <br /> подписку
              </SubscriptionLink>
              <SubscriptionLink theme="info" to={linkEvents}>
                Смотреть список
                <br /> турниров
              </SubscriptionLink>
            </>
          )}
          {!isActive && (
            <>
              <SubscriptionLink theme="subscription" to={linkPrices}>
                Купить
                <br /> подписку
              </SubscriptionLink>
              <SubscriptionLink theme="info" to={linkInfo}>
                Узнать
                <br /> подробнее
              </SubscriptionLink>
            </>
          )}
        </div>
      )}
      {loading && (
        <div className={styles.actions}>
          <div className={cx(styles.button, styles.theme_skeleton)}></div>
          <div className={cx(styles.button, styles.theme_skeleton)}></div>
        </div>
      )}
    </section>
  );
}

function SubscriptionLink({
  className,
  theme,
  to,
  target,
  rel,
  children,
}: {
  className?: string;
  theme: 'subscription' | 'info';
  to: string;
  target?: HTMLLinkElement['target'];
  rel?: HTMLLinkElement['rel'];
  children: ReactNode;
}) {
  return (
    <Link
      className={cx(styles.button, className, styles[`theme_${theme}`])}
      to={to}
      target={target}
      rel={rel}
    >
      <span>{children}</span>
      <ArrowSlimIcon />
    </Link>
  );
}
